import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Storefront, ArrowsClockwise, PaperPlaneTilt } from "@phosphor-icons/react";

const cls = (s) => /fail|reject|error/.test(s) ? "border-red-600 text-red-500" : /live|deliver|complete|success/.test(s) ? "border-green-500 text-green-500" : "border-zinc-600 text-zinc-400";

export default function StoreStatus({ release, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const d = release.delivery || {};
  const stores = info?.stores ?? d.stores ?? [];
  const failed = stores.filter((s) => /fail|reject|error/.test(s.status)).length;

  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/releases/${release.id}/stores`);
      setInfo(data);
      toast[data.exposed ? "success" : "message"](data.exposed ? `${data.stores.length} store statuses from Too Lost` : "Too Lost API returned no per-store breakdown for this release");
      onRefreshed?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Store check failed"); }
    finally { setBusy(false); }
  };

  const requestRedelivery = async () => {
    const note = window.prompt("Request redelivery from Too Lost support.\n\nOptional: paste the error text shown in Too Lost's Delivery tab (sent with the UPC, release ID and failed stores):");
    if (note === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/releases/${release.id}/redeliver-request`, { note: note.trim() || null });
      toast.success(`Redelivery request sent to ${data.sent_to.join(" + ")}`);
      onRefreshed?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-6 py-4 bg-zinc-950 border-t border-zinc-900" data-testid={`store-status-${release.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
          <Storefront size={12} className="text-red-500" /> Store delivery
          {stores.length > 0 && <span className={failed ? "text-red-500" : "text-green-500"}>· {failed ? `${failed} failed` : `${stores.length} ok`}</span>}
          {(info?.checked_at || d.stores_checked_at) && <span className="text-zinc-600 normal-case tracking-normal">checked {new Date(info?.checked_at || d.stores_checked_at).toLocaleString()}</span>}
        </div>
        <div className="flex items-center gap-2">
          {d.redelivery_requested_at && <span className="text-[10px] text-zinc-500" data-testid={`redelivery-requested-${release.id}`}>Redelivery requested {new Date(d.redelivery_requested_at).toLocaleDateString()}</span>}
          <button onClick={requestRedelivery} disabled={busy || !d.toolost_release_id} data-testid={`redeliver-request-${release.id}`} className="border border-red-600 text-red-500 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:bg-red-600 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
            <PaperPlaneTilt size={12} /> Request redelivery
          </button>
          <button onClick={check} disabled={busy || !d.toolost_release_id} data-testid={`store-check-${release.id}`} className="border border-zinc-700 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-white inline-flex items-center gap-1 disabled:opacity-40">
            <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /> Check stores
          </button>
        </div>
      </div>
      {stores.length === 0 ? (
        <div className="text-xs text-zinc-600 leading-relaxed" data-testid={`store-status-empty-${release.id}`}>
          {info && !info.exposed
            ? <>Too Lost's API doesn't include a per-store breakdown for this release (fields: <span className="font-mono">{(info.top_level_keys || []).join(", ")}</span>). Per-store results are only in the Too Lost portal → release → <span className="text-zinc-300">Delivery</span>. When every store fails at once, it's a Too Lost packaging/asset failure, not store rejection — ask Too Lost to redeliver UPC {d.upc || "—"}.</>
            : "No per-store statuses yet — click Check stores."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {stores.map((s, i) => (
            <div key={`${s.store}-${i}`} className="border border-zinc-800 px-3 py-2 text-xs flex items-start justify-between gap-2" data-testid={`store-row-${release.id}-${i}`}>
              <div className="min-w-0"><div className="truncate">{s.store}</div>{s.message && <div className="text-[10px] text-zinc-500 mt-0.5">{s.message}</div>}{s.date && <div className="text-[10px] text-zinc-600 font-mono">{s.date}</div>}</div>
              <span className={`text-[9px] tracking-widest uppercase border px-1.5 py-0.5 shrink-0 ${cls(s.status)}`}>{s.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
